import { useMemo } from 'react';

import { useHdrProcessingRateMins } from '@/api/devices';
import {
    PostHistoricalSurfaceTemperatureDataPayload,
    PostHistoricalSurfaceTemperatureDataResponse,
    SeaSurfaceTemperatureItemFormattedWithUnits,
    SurfaceTemperatureHistoryItem,
} from '@/api/historicalData';
import { combineProcessingSources } from '@/api/historicalData/helpers';
import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { convert } from '@/common/convert';
import { CombinedQueryResult, QueryConfig } from '@/common/react-query';
import { HISTORICAL_SURFACE_TEMPERATURE_DATA_QUERY_KEY } from '@/config';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';
import { DateRange, useGlobalStore } from '@/store';
import { maybeMockNestedHdrResponse } from '@/utils/mockDataHelpers';

export const postHistoricalSurfaceTemperatureData = async (
    payload: PostHistoricalSurfaceTemperatureDataPayload,
    options?: RequestOptions
): Promise<SurfaceTemperatureHistoryItem[]> => {
    const response: PostHistoricalSurfaceTemperatureDataResponse = await maybeMockNestedHdrResponse(
        axios.post('/fetch/surface-temperature-data-history', payload, {
            ...requestConfig(options),
        }),
        'sstHistory'
    );

    return response.data.sstHistory.map(({ location, ...item }) => ({ ...item, ...location }));
};

type UseHistoricalSurfaceTemperatureDataOptions = Omit<
    PostHistoricalSurfaceTemperatureDataPayload,
    'spotterId' | 'startDate' | 'endDate'
> & {
    spotterId?: string | null;
    dateRange?: DateRange | null;
    config?: Omit<QueryConfig<typeof postHistoricalSurfaceTemperatureData>, 'select'>;
};

/**
 * Historical sea surface temperature for the range, in the user's preferred units, with embedded samples
 * filling the gaps between the hdr ones.
 */
export const useHistoricalSurfaceTemperatureData = ({
    spotterId,
    dateRange,
    processingSources,
    config,
}: UseHistoricalSurfaceTemperatureDataOptions): CombinedQueryResult<SeaSurfaceTemperatureItemFormattedWithUnits[]> => {
    const processingSource = useGlobalStore((state) => state.processingSource);
    const { data: surfaceTemperatureDataRaw, ...rest } = useLiveSplitQuery({
        queryKeyPrefix: HISTORICAL_SURFACE_TEMPERATURE_DATA_QUERY_KEY,
        spotterId,
        dateRange,
        keyOptions: { processingSources },
        fetchRange: ({ viewToken, signal, ...range }) =>
            postHistoricalSurfaceTemperatureData({ ...range, processingSources }, { viewToken, signal }),
        // one of the older Spotter entrypoints, so it excludes the bounds it is given
        apiDateBounds: 'exclusive',
        timestampKey: 'timestamp',
        sortOrder: 'asc',
        /*
         * wavefleet queries a window once per device row and concatenates the results. The sort it finishes
         * with keys on a top level `timestamp`, which these samples carry under `location`, so it does not
         * order them: a spotter whose device was replaced answers in device order, not time order.
         */
        responseSortOrder: 'unsorted',
        config,
    });
    const hdrProcessingRateMins = useHdrProcessingRateMins({
        spotterId,
        config: { enabled: config?.enabled !== false },
    });
    const units = usePreferredUnits('temperature');
    const formatted = useMemo<SeaSurfaceTemperatureItemFormattedWithUnits[] | undefined>(
        () =>
            combineProcessingSources(
                surfaceTemperatureDataRaw?.map((item) => {
                    return {
                        degrees: convert(item.degrees).from('C').to(units),
                        timestamp: item.timestamp,
                        processing_source: item.processing_source,
                        units: units,
                        unitType: 'temperature',
                    };
                }),
                hdrProcessingRateMins,
                processingSource
            ),
        [hdrProcessingRateMins, surfaceTemperatureDataRaw, units, processingSource]
    );

    return { data: formatted, ...rest };
};
